<template>
  <main class="employee-profile">
    <div class="employee-profile__container">
      <button-back />
      <div class="employee-profile__head">
        <div class="employee-profile__head__row">
          <h1 class="employee-profile__head__title">
            Сотрудник: <span v-if="isDataLoaded">{{ employee.name }}</span>
            <span v-else class="skeleton skeleton-text" />
          </h1>
          <slot name="head" />
        </div>
        <p
          class="employee-profile__head__status"
          v-if="employee.isDismissed"
        >
          Уволен: {{ employee.dismissedDate }}
        </p>
      </div>
      <div class="employee-tabs tabs-fixed">
        <template v-if="isDataLoaded">
          <router-link
            v-for="item in tabs"
            v-bind:key="item.name"
            active-class="select"
            class="employee-tabs__item"
            v-bind:to="{ name: item.name, params: { employeeId: employeeId } }"
          >
            {{item.text}}
          </router-link>
        </template>
        <template v-else>
          <div
            class="employee-tabs__item skeleton skeleton-text"
            v-for="i in 3"
            :key="i"
          />
        </template>
      </div>
      <slot />
    </div>
  </main>
</template>

<script>
import { EMPLOYEE_PROFILE } from '@/store/actions/employee';
import ButtonBack from '@/components/ButtonBack.vue';

const menuItems = [
  {
    id: 'basic',
    text: 'Основное',
    notAllowsForRoles: [],
    name: 'ClinicPanelEmployeeProfileBasic',
  },
  {
    id: 'admission',
    text: 'Прием',
    notAllowsForRoles: ['admin', 'assistant'],
    name: 'ClinicPanelEmployeeProfileAdmission',
  },
  {
    id: 'payroll-schema',
    auth: 'payroll',
    notAllowsForRoles: [],
    text: 'Схема расчета ЗП',
    name: 'ClinicPanelEmployeePayrollSchema',
  },
  {
    id: 'permissions',
    text: 'Права',
    notAllowsForRoles: [],
    name: 'ClinicPanelEmployeeProfilePermissions',
  },
  {
    id: 'egisz',
    text: 'ЕГИСЗ',
    notAllowsForRoles: ['assistant', 'admin'],
    name: 'ClinicPanelEmployeeProfileEgisz',
  },
  {
    auth: 'branch-system',
    id: 'work-places',
    text: 'Рабочее место',
    notAllowsForRoles: [],
    name: 'ClinicPanelEmployeeProfileWorkPlace',
  },
];

/**
 * @description Компонент профиля сотрудника.
 * @type {Vue}
 */
export default {
  components: { ButtonBack },
  computed: {
    employee: (vm) => vm.$store.getters.employee,
    employeeId: ({ $route }) => parseInt($route.params.id, 10),
    tabs() {
      return this.$options.menuItems.flatMap((candidate) => {
        if (candidate.auth && !this.$store.getters.isSectionAvailable(candidate.auth)) {
          return [];
        }
        if ((this.selectedRole !== '' || this.role !== '') && candidate.notAllowsForRoles !== null) {
          return candidate.notAllowsForRoles.includes(this.selectedRole || this.role)
            ? []
            : candidate;
        }
        return [candidate];
      });
    },
  },
  created() {
    const setEmployee = (employee) => {
      this.$store.commit(EMPLOYEE_PROFILE, employee);
      this.role = employee.role;
    };
    this.fetchEmployee(this.employeeId).then((result) => {
      result.forEach(setEmployee);
      this.isDataLoaded = true;
    });
  },
  data() {
    return {
      isDataLoaded: false,
      role: '',
    };
  },
  menuItems,
  methods: {
    /**
     * @description Получить пациента.
     * @param {number} id Идентификатор пациента.
     * @returns {Promise<Either>}
     */
    fetchEmployee(id) {
      return this.$employeeGateway.getEmployeeName(id);
    },
  },
  name: 'ClinicPanelEmployeeProfile',
  props: {
    /**
     * @description Выбранная роль
     * @type String
     */
    selectedRole: {
      type: String,
      required: false,
      default: () => '',
    },
  },
};
</script>

<style lang="scss" scoped>

.employee {
  &-profile {
    flex-direction: row;
    display: flex;
    font-family: "Lato", sans-serif;
    flex: 1;
    overflow: hidden;
    &__container {
      display: flex;
      flex-direction: column;
      width: 100%;
      padding: 38px 20px 160px 23px;
      overflow-y: auto;
    }
    &__head {
      display: flex;
      flex-direction: column;
      gap: 10px;
      margin: 0 0 10px 0;
      width: 100%;
      &__title {
        font-size: 38px;
        line-height: 45.6px;
        font-weight: 600;
        color: #90a0b7;
        span {
          color: #192a3e;
        }
      }
      &__status {
        color: #707683;
        font-size: 14px;
        font-weight: 400;
        line-height: 17px;
      }
      &__row {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        align-items: flex-start;
      }
    }
    &__button-back {
      color: var(--action-color);
      display: flex;
      align-items: center;
      gap: 7px;
      text-decoration: none;
      &:before {
        content: '';
        background-image: url('@/assets/img/arrow/arrow-blue.svg');
        background-position: center;
        background-size: contain;
        width: 12px;
        height: 12px;
        display: block;
      }
      &:hover {
        text-decoration: underline;
      }
    }
  }
  &-tabs {
    width: 100%;
    flex-direction: row;
    display: flex;
    gap: 35px;
    border-bottom: 1px solid #c2cfe0;
    a {
      font-size: 18px;
      font-weight: 400;
      line-height: 22px;
    }
    &__item {
      cursor: pointer;
      color: #192a3e !important;
      font-size: 18px;
      font-weight: 400;
      line-height: 22px;
      padding-bottom: 9px;
      text-decoration: none;
      font-family: 'Lato', sans-serif;
      border-bottom: 2px solid transparent;
      &:hover {
        color: #0489e0 !important;
      }
      &.select {
        color: #0489e0 !important;
        transition: color var(--animate-duration);
        border-bottom: 2px solid #0489e0;
        font-size: 18px;
        font-weight: 400;
        line-height: 22px;
        font-family: 'Lato', sans-serif;
      }
    }
  }
}

@media screen and (max-width: 1530px) {
  .employee {
    &-tabs {
      flex-wrap: wrap;
      gap: 5px 20px;
      border-bottom: none;
      &__item {
        padding-bottom: 3px;
      }
    }
  }
}

.button-back {
  color: var(--action-color);
  display: flex;
  align-items: center;
  gap: 7px;
  text-decoration: none;
  margin: 0 0 10px 0;
  &:before {
    content: '';
    background-image: url('@/assets/img/arrow/arrow-blue.svg');
    background-position: center;
    background-size: contain;
    width: 12px;
    height: 12px;
    display: block;
  }
  &:hover {
    text-decoration: underline;
  }
}

@media screen and (max-width: 1310px) {
  .employee-profile__head {
    &__row {
      flex-direction: column-reverse;
      gap: 20px;
    }
  }
}

@media screen and (min-width: 960px){
  .tabs-fixed {
    background-color: white;
    position: sticky;
    z-index: 2;
    top: -40px;
    padding: 10px 0 0 0;
  }
}

@media screen and (max-width: 480px) {
  .employee {
    &-tabs {
      &__item {
        font-size: 16px;
        right: 30px;
      }
    }
  }
}

.skeleton {
  background-color: white;
  border-radius: 4px;
  display: inline-block;
  height: 1em;
}
.skeleton-text {
  width: 100px;
}
.employee-tabs__item.skeleton {
  height: 20px;
  margin: 5px 0;
  width: 100px;
}
</style>
